<template>
  <div class="container">
    <header class="header">
      <h2>Orders & Refunds</h2>
      <div class="toolbar">
        <label class="inline strong">
          <span class="label">University</span>
          <select class="input" v-model="selectedSlug" aria-label="University" @change="onUniversityChange">
            <option value="">All (permitted)</option>
            <option v-for="u in universities" :key="u.slug" :value="u.slug">{{ u.name }} ({{ u.slug }})</option>
          </select>
        </label>
        <details class="filters" ref="filters">
          <summary class="btn">Filters</summary>
          <div class="panel">
            <div class="row">
              <label>
                <span class="label">From (ceremony)</span>
                <input type="date" v-model="fromDate" aria-label="From date" />
              </label>
              <label>
                <span class="label">To (ceremony)</span>
                <input type="date" v-model="toDate" aria-label="To date" />
              </label>
              <label>
                <span class="label">Preset</span>
                <select v-model="preset" @change="onPreset">
                  <option value="">Custom</option>
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                </select>
              </label>
            </div>
            <div class="row" v-if="ceremonies && ceremonies.length">
              <label>
                <span class="label">Ceremony from</span>
                <select class="input" v-model="ceremonyFromId" aria-label="Start ceremony">
                  <option value="">Any</option>
                  <option v-for="c in ceremonies" :key="c.id" :value="c.id">{{ fmtCer(c) }}</option>
                </select>
              </label>
              <label>
                <span class="label">Ceremony to</span>
                <select class="input" v-model="ceremonyToId" aria-label="End ceremony">
                  <option value="">Any</option>
                  <option v-for="c in ceremonies" :key="c.id" :value="c.id">{{ fmtCer(c) }}</option>
                </select>
              </label>
            </div>
            <p class="small-note">Date range filters by ceremony date/time (not order creation date).</p>
            <div class="row">
              <label>
                <span class="label">Completed from <span class="hint">(uses order updated time)</span></span>
                <input type="date" v-model="completedFrom" aria-label="Completed from" />
              </label>
              <label>
                <span class="label">Completed to</span>
                <input type="date" v-model="completedTo" aria-label="Completed to" />
              </label>
            </div>
            <div class="row">
              <label>
                <span class="label">Product <span class="hint">(Bundles not split yet)</span></span>
                <select class="input" v-model="product" aria-label="Product type">
                  <option value="all">All</option>
                  <option value="clip">Clip</option>
                  <option value="dd">Download</option>
                  <option value="bundle" disabled title="Bundle filtering will arrive after we persist is_bundle in orders">Bundle (coming soon)</option>
                </select>
              </label>
            </div>
            <p class="small-note">Funnel is deduped by Stripe checkout session across products. Bundle-specific filtering is not available yet.</p>
            <div class="row actions">
              <button class="btn" @click="resetFilters">Reset</button>
              <button class="btn primary" @click="applyFilters" aria-label="Apply filters">Apply</button>
            </div>
          </div>
        </details>
      </div>
    </header>

    <div v-if="loading">Loading…</div>
    <div v-else>
      <div class="cards">
        <div class="card">
          <div class="card-title">Clip Orders</div>
          <div class="card-value">{{ metrics.totals?.orders_clip || 0 }}</div>
        </div>
        <div class="card">
          <div class="card-title">Digital Downloads</div>
          <div class="card-value">{{ metrics.totals?.orders_dd || 0 }}</div>
        </div>
        <div class="card">
          <div class="card-title">POS Orders</div>
          <div class="card-value">{{ metrics.totals?.orders_pos || 0 }}</div>
        </div>
        <div class="card">
          <div class="card-title">Refunds</div>
          <div class="card-value">{{ metrics.totals?.refunds_total || 0 }}</div>
        </div>
        <div class="card">
          <div class="card-title">Revenue (gross)</div>
          <div class="card-value">
            <template v-if="metrics.revenue_currency && metrics.revenue_currency !== 'mixed'">
              {{ fmtCurrency(metrics.revenue_total, metrics.revenue_currency) }}
            </template>
            <template v-else>
              {{ fmtCurrency(metrics.revenue_total, defaultCurrency) }}
            </template>
          </div>
          <div v-if="metrics.revenue_currency === 'mixed'" class="note">Multiple currencies</div>
        </div>
      </div>

      <section aria-label="Funnel" class="funnel">
        <div class="metric">
          <div class="metric-head">
            <span class="metric-title">Started</span>
            <span class="metric-value">{{ started }}</span>
          </div>
          <div class="bar">
            <div class="bar-fill neutral" :style="{width: '100%'}"></div>
          </div>
        </div>
        <div class="metric">
          <div class="metric-head">
            <span class="metric-title">Paid</span>
            <span class="metric-value">{{ paid }} <span class="sub">({{ (paidPct*100).toFixed(0) }}%)</span></span>
          </div>
          <div class="bar" :title="`Paid: ${paid} (${(paidPct*100).toFixed(0)}%)`">
            <div class="bar-fill primary" :style="{width: (paidPct*100)+'%'}"></div>
          </div>
        </div>
        <div class="metric">
          <div class="metric-head">
            <span class="metric-title">Refunded</span>
            <span class="metric-value">{{ refunded }} <span class="sub">({{ (refundedPctOfPaid*100).toFixed(0) }}% of paid)</span></span>
          </div>
          <div class="bar" :title="`Refunded: ${refunded} (${(refundedPctOfPaid*100).toFixed(0)}% of paid)`">
            <div class="bar-fill danger" :style="{width: (refundedPctOfPaid*100)+'%'}"></div>
          </div>
        </div>
      </section>

      <h3>Sales Volume</h3>
      <sales-volume-chart v-if="metrics.timeseries && metrics.timeseries.length" :series="metrics.timeseries" />
      <div v-else class="muted">No daily sales data for filters.</div>

      <details :open="showDetails">
        <summary @click.prevent="toggleDetails" class="summary">
          <span>{{ showDetails ? 'Hide' : 'Show' }} ceremony table</span>
        </summary>
        <div class="table-controls">
          <span>Page:</span>
          <button class="btn" @click="prevPage" :disabled="offset === 0">Prev</button>
          <button class="btn" @click="nextPage" :disabled="!hasNext">Next</button>
          <span class="muted" v-if="metrics.by_ceremony_total">Total: {{ metrics.by_ceremony_total }}</span>
        </div>
        <table class="table" aria-label="Per-ceremony orders">
          <thead>
            <tr>
              <th class="th-sort" @click="sortByHeader('ceremony_datetime')">Ceremony</th>
              <th class="th-sort" @click="sortByHeader('orders')">Clip Orders</th>
              <th class="th-sort" @click="sortByHeader('refunds')">Refunded</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in metrics.by_ceremony || []" :key="row.ceremony_id">
              <td>{{ row.ceremony_datetime }}</td>
              <td>{{ row.orders_clip }}</td>
              <td>{{ row.orders_clip_refunded }}</td>
            </tr>
          </tbody>
        </table>
      </details>

      <h3>Prices <small v-if="!selectedSlug" style="font-weight: normal; color: #666">Select a university to view</small></h3>
      <div class="cards" v-if="prices">
        <div class="card">
          <div class="card-title">Clip</div>
          <div class="card-value">{{ fmtP(prices.clip) }}</div>
        </div>
        <div class="card">
          <div class="card-title">Digital Download</div>
          <div class="card-value">{{ fmtP(prices.digital_download) }}</div>
        </div>
        <div class="card">
          <div class="card-title">Bundle (Clip + Add-on)</div>
          <div class="card-value">{{ fmtP(prices.bundle) }}</div>
        </div>
      </div>

      <h3>Product Types</h3>
      <ul class="list">
        <li>Clip: {{ metrics.product_types?.clip || 0 }}</li>
        <li>Digital Download: {{ metrics.product_types?.digital_download || 0 }}</li>
        <li>POS: {{ metrics.product_types?.pos || 0 }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import SalesVolumeChart from './SalesVolumeChart.vue'

export default {
  name: 'OrdersAnalytics',
  data() {
    return {
      universities: [],
      selectedSlug: '',
      loading: false,
      metrics: {},
      prices: null,
      fromDate: '',
      toDate: '',
      completedFrom: '',
      completedTo: '',
      ceremonyFromId: '',
      ceremonyToId: '',
      ceremonies: [],
      sortBy: 'ceremony_datetime',
      sortDir: 'desc',
      limit: 20,
      offset: 0,
      showDetails: false,
      preset: '',
      product: 'all',
    }
  },
  components: { SalesVolumeChart },
  created() {
    this.fetchUniversities().then(() => this.fetchMetrics())
  },
  methods: {
    handleAuthError(e) {
      try {
        const status = (e && e.response && e.response.status) || 0
        if (status === 401 || status === 403) {
          try { localStorage.removeItem('token') } catch (err) { /* no-op */ }
          this.$router.push({ path: '/admin/login' })
          return true
        }
      } catch (err) { /* no-op */ }
      return false
    },
    async fetchUniversities() {
      try {
        const r = await axios.get('/universities')
        this.universities = Array.isArray(r.data) ? r.data : []
      } catch (e) {
        if (this.handleAuthError(e)) return
        this.universities = []
      }
    },
    async onUniversityChange() {
      await Promise.all([this.fetchMetrics(), this.fetchPrices(), this.fetchCeremonies()])
    },
    async fetchMetrics() {
      this.loading = true
      try {
        const qs = new URLSearchParams()
        if (this.selectedSlug) qs.set('university_slug', this.selectedSlug)
        if (this.fromDate) qs.set('from', this.fromDate)
        if (this.toDate) qs.set('to', this.toDate)
        if (this.completedFrom) qs.set('completed_from', this.completedFrom)
        if (this.completedTo) qs.set('completed_to', this.completedTo)
        if (this.ceremonyFromId) qs.set('ceremony_from', this.ceremonyFromId)
        if (this.ceremonyToId) qs.set('ceremony_to', this.ceremonyToId)
        if (this.sortBy) qs.set('sort_by', this.sortBy)
        if (this.sortDir) qs.set('sort_dir', this.sortDir)
        if (this.limit) qs.set('limit', String(this.limit))
        if (this.offset) qs.set('offset', String(this.offset))
        if (this.product) qs.set('product', this.product)
        const q = qs.toString() ? `?${qs.toString()}` : ''
        const r = await axios.get(`/analytics/orders${q}`)
        this.metrics = r.data || {}
      } catch (e) {
        if (this.handleAuthError(e)) return
        this.metrics = {}
        this.prices = null
      } finally {
        this.loading = false
      }
    },
    async fetchCeremonies() {
      this.ceremonies = []
      if (!this.selectedSlug) return
      const uni = this.universities.find(u => u.slug === this.selectedSlug)
      if (!uni) return
      try {
        const r = await axios.get(`/universities/${uni.id}`)
        const list = (r.data && r.data.ceremonies) || []
        this.ceremonies = list.slice().sort((a, b) => new Date(a.datetime) - new Date(b.datetime))
      } catch (e) {
        if (this.handleAuthError(e)) return
        this.ceremonies = []
      }
    },
    async fetchPrices() {
      this.prices = null
      if (!this.selectedSlug) return
      const uni = this.universities.find(u => u.slug === this.selectedSlug)
      if (!uni) return
      try {
        const r = await axios.get(`/universities/${uni.id}/prices`)
        this.prices = r.data || null
      } catch (e) {
        if (this.handleAuthError(e)) return
        this.prices = null
      }
    },
    applyFilters() {
      this.offset = 0
      this.fetchMetrics()
    },
    onPreset() {
      const days = Number(this.preset)
      if (!days) return
      const to = new Date()
      const from = new Date()
      from.setDate(to.getDate() - days)
      this.fromDate = from.toISOString().slice(0, 10)
      this.toDate = to.toISOString().slice(0, 10)
    },
    resetFilters() {
      this.fromDate = ''
      this.toDate = ''
      this.completedFrom = ''
      this.completedTo = ''
      this.ceremonyFromId = ''
      this.ceremonyToId = ''
      this.preset = ''
      this.product = 'all'
    },
    toggleDetails() {
      this.showDetails = !this.showDetails
    },
    nextPage() {
      if (!this.hasNext) return
      this.offset += this.limit
      this.fetchMetrics()
    },
    prevPage() {
      if (this.offset === 0) return
      this.offset = Math.max(0, this.offset - this.limit)
      this.fetchMetrics()
    },
    sortByHeader(key) {
      if (this.sortBy === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = key
        this.sortDir = 'desc'
      }
      this.offset = 0
      this.fetchMetrics()
    },
    currencySymbol(code) {
      const map = { gbp: '£', usd: '$', eur: '€', aud: 'A$', cad: 'C$', nzd: 'NZ$', chf: 'CHF', jpy: '¥' }
      return map[(code || '').toLowerCase()] || ''
    },
    fmtP(p) {
      if (!p || typeof p.price !== 'number') return '—'
      const sym = this.currencySymbol(p.currency)
      try { return `${sym}${(p.price/100).toFixed(2)}` } catch { return '—' }
    },
    fmtCurrency(cents, currency) {
      if (!cents && cents !== 0) return '—'
      const sym = this.currencySymbol(currency)
      try { return `${sym}${(Number(cents)/100).toFixed(2)}` } catch { return '—' }
    },
    fmtCer(c) {
      try { return new Date(c.datetime).toLocaleString() } catch { return c.id }
    },
    wedgeStyle(pct) {
      const clamped = Math.max(0.05, Math.min(1, pct))
      return { transform: `scaleX(${clamped})` }
    },
  },
  computed: {
    tenantSlug() {
      try {
        const raw = localStorage.getItem('token') || ''
        const part = raw.split('.')[1]
        if (!part) return ''
        const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
        const payload = JSON.parse(decodeURIComponent(escape(json)))
        return payload.tenant || ''
      } catch (e) { return '' }
    },
    started() { return this.metrics.funnel?.orders_started || 0 },
    paid() { return this.metrics.funnel?.orders_paid || 0 },
    refunded() { return this.metrics.funnel?.orders_refunded || 0 },
    paidPct() { return this.started ? this.paid / this.started : 0 },
    refundedPctOfPaid() { return this.paid ? this.refunded / this.paid : 0 },
    defaultCurrency() {
      // Try to infer from prices panel when available
      if (this.tenantSlug === 'gfpevents') return 'aud'
      if (this.prices && this.prices.clip && this.prices.clip.currency) return this.prices.clip.currency
      if (this.prices && this.prices.digital_download && this.prices.digital_download.currency) return this.prices.digital_download.currency
      return 'gbp'
    },
    hasNext() {
      const total = this.metrics.by_ceremony_total
      if (typeof total === 'number') return this.offset + this.limit < total
      const rows = this.metrics.by_ceremony || []
      return rows.length === this.limit
    },
    funnelAria() {
      return `Started ${this.started}, Paid ${this.paid}, Refunded ${this.refunded}`
    }
  }
}
</script>

<style scoped>
.container { max-width: 1040px; margin: 0 auto; padding: 8px 12px; }
.header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 8px 0 12px; }
.toolbar { display: flex; align-items: center; gap: 8px; }
.inline { display: inline-flex; align-items: center; gap: 6px; }
.inline.strong .label { color: #111827; font-weight: 600; }
.input { height: 36px; padding: 6px 12px; border: 1px solid #94a3b8; border-radius: 8px; background: #fff; color: #111827; }
.label { display: block; font-size: 12px; color: #6b7280; }
.hint { color: #9ca3af; font-weight: 500; }
.small-note { margin: 4px 0 0; font-size: 12px; color: #6b7280; }
.filters { position: relative; }
.filters[open] > summary { border-bottom-left-radius: 0; border-bottom-right-radius: 0; }
.filters > summary::marker { display: none; }
.filters > summary::-webkit-details-marker { display: none; }
.filters > summary { list-style: none; }
.panel { position: absolute; right: 0; top: 100%; z-index: 10; min-width: 520px; padding: 12px; background: #fff; border: 1px solid #e5e7eb; border-radius: 6px; box-shadow: 0 6px 20px rgba(0,0,0,.08); }
.panel .row { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-bottom: 8px; }
.panel .row.actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 4px; }
.btn { padding: 6px 10px; border: 1px solid #e5e7eb; background: #f9fafb; border-radius: 6px; cursor: pointer; font-size: 0.9rem; }
.btn.primary { background: #111827; color: #fff; border-color: #111827; }
.cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; margin-bottom: 12px; }
.card { padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; }
.card-title { font-size: 12px; color: #6b7280; }
.card-value { font-size: 20px; font-weight: 600; margin-top: 6px; color: #111827; }
.funnel { display: grid; gap: 10px; margin: 16px 0 12px; }
.metric { display: flex; flex-direction: column; gap: 6px; }
.metric-head { display: flex; align-items: baseline; justify-content: space-between; }
.metric-title { font-size: 12px; color: #6b7280; }
.metric-value { font-size: 18px; font-weight: 600; color: #111827; }
.metric-value .sub { font-size: 12px; color: #6b7280; font-weight: 500; margin-left: 6px; }
.bar { height: 8px; background: #f3f4f6; border-radius: 9999px; overflow: hidden; }
.bar-fill { height: 100%; border-radius: 9999px; transition: width .3s ease; }
.bar-fill.neutral { background: #d1d5db; }
.bar-fill.primary { background: #2563eb; }
.bar-fill.danger { background: #ef4444; }
.table { width: 100%; border-collapse: collapse; margin-top: 12px; }
.table th, .table td { border-bottom: 1px solid #f3f4f6; padding: 10px 8px; text-align: left; }
.th-sort { cursor: pointer; user-select: none; }
.list { margin-top: 8px; }
.table-controls { display: flex; align-items: center; gap: 8px; margin: 8px 0; }
.muted { color: #6b7280; }
.summary { cursor: pointer; user-select: none; margin: 12px 0; }
</style>
